/**
 * The Trace theme vocabulary — the standardized property names a style sets.
 *
 * This is the **single source of truth** for what a theme is made of. It is not
 * an editor convenience: authored content already conforms to it (design-set
 * GLBs ship materials literally named `Structure` / `Accent_01`, which adoption
 * reads through the alias table), and every other client that themes the same
 * assets — the sandbox extractor, the AI tools, CMS validation, the Unity
 * runtime — has to spell these the same way. So the vocabulary is a contract,
 * and it is published as one: `npm run generate:theme-vocabulary` emits
 * `schemas/theme-vocabulary.json` from this module for consumers that cannot
 * import TypeScript.
 *
 * It is deliberately *not* Canopy grammar. Nothing in resolution treats these
 * names specially — they are ordinary property names, and a runtime that knows
 * nothing about them renders a themed scene correctly. See
 * `docs/STYLES.md` §Vocabulary for the normative statement.
 *
 * ## Three axes
 *
 * | Axis | Roles | Suffixes | Example |
 * |---|---|---|---|
 * | **Material** | 7 | `Src` `Color` `Roughness` `Metallic` | `structureSrc` |
 * | **Surface** | 11 | `Color` `TextColor` `Finish` | `panelTextColor` |
 * | **Text** | 4 | `Font` `Weight` `Scale` `LineHeight` | `titleFont` |
 *
 * A role is a *name* plus a family of suffixed properties, and that shape is
 * shared across axes on purpose: one rule ("role + suffix → property name")
 * describes the whole vocabulary, so adding an axis adds data, not machinery.
 *
 * The two role-name sets are disjoint, which is what lets both use a `Color`
 * suffix without ambiguity. Note `accentColor` (a surface) and `accent1Color`
 * (a material) are different properties — the surface axis names UI surfaces,
 * the material axis names parts of a physical object.
 */

import { MATERIAL_ROLES, ROLE_SUFFIXES, normalizeRoleKey, rolePropertyName } from './material-roles';
import type { MaterialRole, RoleSuffix } from './material-roles';
import type { PropertyDeclaration, StringPropertyKind } from './behaviors/propertiesdata';

export { MATERIAL_ROLES, ROLE_SUFFIXES, rolePropertyName };
export type { MaterialRole, RoleSuffix };

// ── Surface roles (the colour axis) ─────────────────────────────────────────

/**
 * The eleven surface roles, in canonical order.
 *
 * A surface is a thing content sits on, so each one carries its own colour,
 * the colour of content *on* it, and a finish — the same triple design-token
 * systems pair as `--background` / `--foreground`.
 *
 * Two roles the source data carried are deliberately absent. `destructive` was
 * byte-identical to `error` in all 28 extracted styles, so standardizing both
 * would enshrine a synonym; `error` wins because it completes the status
 * quadruple with `success` / `warning` / `info`. And the flat `palette` block
 * that sat beside these tokens is dropped entirely: it disagreed with them on
 * the same colour in roughly a third of styles, and it could not express the
 * foreground pairing, so it was a lossy copy that had drifted.
 */
export const SURFACE_ROLES = [
    'background',
    'panel',
    'card',
    'muted',
    'primary',
    'secondary',
    'accent',
    'success',
    'warning',
    'error',
    'info',
] as const;

export type SurfaceRole = typeof SURFACE_ROLES[number];

/** Property suffixes a surface role carries. */
export const SURFACE_SUFFIXES = ['color', 'textColor', 'finish'] as const;

export type SurfaceSuffix = typeof SURFACE_SUFFIXES[number];

/**
 * How a surface renders in 3D. A small closed set on purpose: a creator picks
 * from it, and every client has to agree what the words mean.
 */
export const SURFACE_FINISHES = ['matte', 'satin', 'gloss'] as const;

export type SurfaceFinish = typeof SURFACE_FINISHES[number];

/** The property a surface role/suffix pair addresses — `panel` + `textColor` → `panelTextColor`. */
export function surfacePropertyName(role: SurfaceRole, suffix: SurfaceSuffix): string {
    return `${role}${suffix.charAt(0).toUpperCase()}${suffix.slice(1)}`;
}

/**
 * The one surface colour that is not a surface: the line between them.
 * Flat rather than a role because a border has no content sitting on it and no
 * finish of its own.
 */
export const BORDER_COLOR_PROPERTY = 'borderColor';

/**
 * GLB material names that declare a surface COLOUR, not a material role.
 *
 * The smart-component kit names most of its slots after the seven material
 * roles, but its background faces are named semantically instead: `fill_BG`
 * (89 of the 256 kit GLBs) is the fill behind a panel or frame's content,
 * `frame_BG` (6 GLBs) is a frame's own backdrop. Left unrecognized, those
 * slots keep the GLB's authored factory value — a gold slab no style can
 * touch — while every named-role slot around them re-themes.
 *
 * They bind to the surface axis because that is what they name: a thing
 * content sits on. Colour only, by construction — a surface role has no
 * src/roughness/metallic family to bind.
 *
 * WHICH role each maps to is the prototype's own answer rather than an
 * inference from the names, because a wrong mapping here fails silently — it
 * binds a real surface colour, just the wrong one. Its slot parser
 * (`AILayout_Prototype/src/materials/materialSlotParser.ts`) reads:
 *
 *     { pattern: /fill[_-]?bg/i,  role: 'panel' }       // generic semantic fill surface
 *     { pattern: /frame[_-]?bg/i, role: 'background' }  // frame interior = page/canvas background
 *
 * An adoption input exactly like `ROLE_ALIASES` in material-roles: recognized
 * when reading an asset, never stored, never published in the vocabulary
 * document. Kept separate from `ROLE_ALIASES` on purpose — a different axis,
 * not a wider list of the same one.
 */
const SURFACE_COLOR_ALIASES: Readonly<Record<string, SurfaceRole>> = {
    fillbg: 'panel',
    framebg: 'background',
};

/**
 * The surface role a GLB material name declares, or `null` if it declares
 * none. The surface-axis sibling of `resolveMaterialRole`: keys are folded
 * through the same {@link normalizeRoleKey}, so `fill_BG`, `Fill_BG` and
 * `fill-bg` are one name. Callers bind the resolved role's `color` property
 * and nothing else — see {@link SURFACE_COLOR_ALIASES}.
 */
export function resolveSurfaceColorRole(materialName: string | undefined): SurfaceRole | null {
    if (!materialName) return null;
    const key = normalizeRoleKey(materialName);
    return Object.hasOwn(SURFACE_COLOR_ALIASES, key) ? SURFACE_COLOR_ALIASES[key] : null;
}

// ── Text roles (the typography axis) ───────────────────────────────────────

/**
 * The text roles. The Studio spec: "There are 3 Fonts per Style — Title,
 * Header, Body. Each font has a role and a relative size scale."
 *
 * `mono` is a fourth, beyond the spec: a third of the extracted styles author
 * one, and dropping it would lose authored intent for no gain. Styles that
 * don't have one simply don't set it.
 */
export const TEXT_ROLES = ['title', 'header', 'body', 'mono'] as const;

export type TextRole = typeof TEXT_ROLES[number];

/**
 * What each text role carries.
 *
 * `font` is a family NAME the runtime resolves to a bundled MSDF face — not a
 * CSS stack, which a 3D text renderer cannot honour.
 *
 * `scale` and `lineHeight` are what the spec's size ladder needs, and a style
 * sets them only to DEVIATE from it. Absence means the standard ladder:
 * Title 1.5× / 1.25, Header 1.25× / 1.4, Body 1× — see
 * {@link TEXT_SCALE_LADDER}. Emitting the ladder into all 28 styles would be 28
 * identical copies of a constant, which is the noise a style vocabulary exists
 * to remove.
 */
export const TEXT_SUFFIXES = ['font', 'weight', 'scale', 'lineHeight'] as const;

export type TextSuffix = typeof TEXT_SUFFIXES[number];

/**
 * The spec's recommended size ladder — what an absent `scale`/`lineHeight`
 * means. Consumers (the layout generator, text sizing) apply this when the
 * style does not override it.
 */
export const TEXT_SCALE_LADDER: Readonly<Record<TextRole, { scale: number; lineHeight: number }>> = {
    title: { scale: 1.5, lineHeight: 1.25 },
    header: { scale: 1.25, lineHeight: 1.4 },
    body: { scale: 1, lineHeight: 1.4 },
    mono: { scale: 1, lineHeight: 1.4 },
};

/** `title` + `font` → `titleFont`. */
export function textPropertyName(role: TextRole, suffix: TextSuffix): string {
    return `${role}${suffix.charAt(0).toUpperCase()}${suffix.slice(1)}`;
}

// ── Declaration shapes ──────────────────────────────────────────────────────

/** The declaration a theme property is carried by: its type, and an asset kind if it holds one. */
export interface DeclarationShape {
    type: PropertyDeclaration['type'];
    kind?: StringPropertyKind;
}

const MATERIAL_SUFFIX_SHAPES: Record<RoleSuffix, DeclarationShape> = {
    src: { type: 'string', kind: 'material' },
    color: { type: 'color' },
    roughness: { type: 'number' },
    metallic: { type: 'number' },
};

const SURFACE_SUFFIX_SHAPES: Record<SurfaceSuffix, DeclarationShape> = {
    color: { type: 'color' },
    textColor: { type: 'color' },
    // A finish name from SURFACE_FINISHES, not a URL — no asset kind.
    finish: { type: 'string' },
};

const TEXT_SUFFIX_SHAPES: Record<TextSuffix, DeclarationShape> = {
    // A bundled family name, not a URL — no asset kind.
    font: { type: 'string' },
    weight: { type: 'number' },
    scale: { type: 'number' },
    lineHeight: { type: 'number' },
};

/**
 * Every theme property name mapped to the declaration that carries it.
 *
 * The vocabulary decides the shape, never the value: `structureSrc: ""` is
 * still a material-kind string, and `accentColor: "#808080"` must not be read
 * back as a plain string because its value happens to parse as one.
 */
export const THEME_PROPERTY_SHAPES: ReadonlyMap<string, DeclarationShape> = new Map<string, DeclarationShape>([
    ...MATERIAL_ROLES.flatMap(role =>
        ROLE_SUFFIXES.map(suffix =>
            [rolePropertyName(role, suffix), MATERIAL_SUFFIX_SHAPES[suffix]] as const),
    ),
    ...SURFACE_ROLES.flatMap(role =>
        SURFACE_SUFFIXES.map(suffix =>
            [surfacePropertyName(role, suffix), SURFACE_SUFFIX_SHAPES[suffix]] as const),
    ),
    [BORDER_COLOR_PROPERTY, { type: 'color' }],
    ...TEXT_ROLES.flatMap(role =>
        TEXT_SUFFIXES.map(suffix =>
            [textPropertyName(role, suffix), TEXT_SUFFIX_SHAPES[suffix]] as const),
    ),
]);

/**
 * Whether a property name belongs to the published vocabulary.
 *
 * A style may set names outside it — partial and bespoke styles are normal —
 * but only these are guaranteed to mean the same thing to every client.
 */
export function isThemeProperty(name: string): boolean {
    return THEME_PROPERTY_SHAPES.has(name);
}

/**
 * Theme declarations pin their scope.
 *
 * Canopy's default hands a name to the **outermost** provider declaring it (the
 * flag exists so an outer scope can parameterize an inner template). Under that
 * default, a style applied to a branch of an already-themed subtree is a silent
 * no-op — the scene root's style keeps winning, however much nearer the
 * branch's is. Styles want the opposite, so every declaration a style writes
 * carries this. With only one provider declaring a name, both settings resolve
 * identically, so this costs nothing in the common case.
 */
export const THEME_DECLARATION_INHERITS = false;

/** The vocabulary as plain data — the shape `schemas/theme-vocabulary.json` publishes. */
export interface ThemeVocabularyDocument {
    version: number;
    materialRoles: readonly string[];
    materialSuffixes: readonly string[];
    surfaceRoles: readonly string[];
    surfaceSuffixes: readonly string[];
    surfaceFinishes: readonly string[];
    textRoles: readonly string[];
    textSuffixes: readonly string[];
    /** What an absent `scale`/`lineHeight` means, per text role. */
    textScaleLadder: Readonly<Record<string, { scale: number; lineHeight: number }>>;
    /** Every published property name → the declaration that carries it. */
    properties: Record<string, DeclarationShape & { inherits: boolean }>;
}

/** The version stamped into the published artifact. Bump when a role is added or removed. */
export const THEME_VOCABULARY_VERSION = 1;

/** Build the publishable document. Used by the generator and by conformance tests. */
export function themeVocabularyDocument(): ThemeVocabularyDocument {
    return {
        version: THEME_VOCABULARY_VERSION,
        materialRoles: [...MATERIAL_ROLES],
        materialSuffixes: [...ROLE_SUFFIXES],
        surfaceRoles: [...SURFACE_ROLES],
        surfaceSuffixes: [...SURFACE_SUFFIXES],
        surfaceFinishes: [...SURFACE_FINISHES],
        textRoles: [...TEXT_ROLES],
        textSuffixes: [...TEXT_SUFFIXES],
        textScaleLadder: TEXT_SCALE_LADDER,
        properties: Object.fromEntries(
            [...THEME_PROPERTY_SHAPES].map(([name, shape]) =>
                [name, { ...shape, inherits: THEME_DECLARATION_INHERITS }]),
        ),
    };
}
